import AppHelmet from "../../../components/AppComponents/AppHelmet";
import ManageUserPermissionsDrawer from "../../../components/Drawers/ManageUserPermissionsDrawer";
import {
  getUserCloudPermissionID,
  getUserCloudPermissions,
} from "../../../utils/UserUtils";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import { useNavigate, useParams } from "react-router-dom";
import InviteUserDrawer from "../../../components/Drawers/InviteUserDrawer";
import { Fragment, useCallback, useMemo, useState } from "react";
import useQueriedRecords from "../../../api/api-hooks/records/useQueriedRecords";
import useCloudPermissions from "../../../hooks/useCloudPermissions";
import useUser from "../../../api/api-hooks/auth/useUser";
import useToggle from "../../../hooks/useToggle";
import useTenantUsers from "../../../api/api-hooks/tenantUsers/useTenantUsers";
import useRemoveUser from "../../../api/api-hooks/tenantUsers/useRemoveUser";
import useAssignCloudOwnership from "../../../api/api-hooks/tenantUsers/useAssignCloudOwnership";
import useUpdateUserCloudPermissions from "../../../api/api-hooks/auth/useUpdateUserCloudPermissions";
import { findWhere } from "underscore";
import TenantAPI from "../../../api/api/TenantAPI";
import { newToastEvent } from "../../../utils/ToastUtil";
import { Col, Container, Row } from "reactstrap";
import AppPageHeader from "../../../components/Headers/AppPageHeader";
import AppNavbar from "../../../components/AppNavbar/AppNavbar";
import ListItem from "../../../components/CRM/ListItem";
import AppTablePagination from "../../../components/AppTable/AppTablePagination";
import SkeletonLoader from "../../../elements/SkeletonLoader";
import RegisteredUserOptions from "../../../components/Options/RegisteredUserOptions";
import PendingUserOptions from "../../../components/Options/PendingUserOptions";
import TransferOwnershipDrawer from "../../../components/Drawers/TransferOwnershipDrawer";
dayjs.extend(advancedFormat);

const pageTabs = [
  {
    title: "Manage Users",
    activeValue: "users",
  },
  {
    title: "User Activity",
    activeValue: "user-activity",
  },
];

const initialQuery = {
  Aggregations: [],
  Fields: ["*"],
  Filters: [],
  OrderBy: {
    Direction: "DESC",
    Field: "Created",
  },
  Pagination: {
    Count: 25,
    Page: 1,
  },
};

const SecurityAndAccess = () => {
  const { tenantID, tab } = useParams();
  const navigate = useNavigate();
  const [query, setQuery] = useState(initialQuery);
  const { records, pagination, recordsLoading, error, errorMsg } =
    useQueriedRecords({
      moduleAPIName: "Feed",
      query: query,
    });
  const { user: currentUser, cPermissions: permissions } = useUser();
  const { cPermissions } = useCloudPermissions();
  const [manageUser, setManageUser] = useState<any>(null); // The user currently being managed in ManagePermissionsDrawer
  const [transferUser, setTransferUser] = useState<any>(null); // The user selected for ownership transfer
  const [inviteUserModal, toggleInviteUserModal] = useToggle();
  const { users, usersLoading } = useTenantUsers();
  const { removeUser } = useRemoveUser();
  const {
    assignCloudOwnership,
    assigningCloudOwnership,
    assignCloudOwnershipError,
  } = useAssignCloudOwnership();
  const {
    updateUserCloudPermissions,
    updatingPermissions,
    updatePermissionsError,
  } = useUpdateUserCloudPermissions();

  const usersMap = useMemo(() => {
    if (!users || !users?.length) return { registered: [], pending: [] };

    const registered: any[] = [];
    const pending: any[] = [];

    users?.forEach((user: any) => {
      if (user.Name !== "") {
        registered.push(user);
      } else {
        pending.push(user);
      }
    });

    return { registered, pending };
  }, [users]);

  const isCurrentUserOwner = useMemo(() => {
    if (!currentUser) return false;

    const currentTenant = findWhere(currentUser.Tenants, {
      TenantID: tenantID,
    });

    const profile: any = getUserCloudPermissions(
      currentUser,
      permissions,
      tenantID
    );

    return currentTenant?.TenantOwner || !!profile?.Owner;
  }, [currentUser, tenantID, permissions]);

  const handleGetPermissionID = useCallback(
    (user: any) => {
      return getUserCloudPermissionID(user, tenantID);
    },
    [tenantID]
  );

  const resendInvite = useCallback((invitedUser: any) => {
    TenantAPI.resendUserInvite(invitedUser.id).then(
      (data) => {
        if (data.data.httpStatus === 200) {
          newToastEvent(
            "success",
            "Successfully resent invite to user for tenant."
          );
        }
      },
      (err) => {
        console.error(err);
      }
    );
  }, []);

  const handleTabChange = (val: any) => {
    navigate(`/clouds/${tenantID}/settings/security-access/${val}`, {
      replace: true,
    });
  };

  return (
    <>
      <AppHelmet title="Security & Access - Settings" />
      <Container fluid>
        <div className="header">
          <div className="header-body">
            <AppPageHeader
              borderless={true}
              title="Security & Access"
              description="View & manage team members in this GrowthCloud"
            />
          </div>
        </div>
        <div className="mt--4">
          <AppNavbar
            tabs={pageTabs}
            activeTab={tab}
            onSelect={handleTabChange}
          />
        </div>
        {tab === "user-activity" && (
          <>
            <div>
              <div className="pt-3 pb-3 mb-3 border-bottom border-lighter">
                <AppPageHeader
                  size="sm"
                  title="User Activity"
                  description="View recent sign-ins from users in your GrowthCloud"
                />
              </div>
            </div>
            {recordsLoading ? (
              <>
                <ListItem loading={true} />
                <ListItem loading={true} />
              </>
            ) : (
              <>
                {records?.length === 0 && (
                  <div>
                    <ListItem loading={true} />
                    <div></div>
                  </div>
                )}
                {records?.length > 0 &&
                  records.map((item: any) => {
                    const impersonate = !!item.DataObject?.ImpersonateBy
                      ? item.DataObject?.ImpersonateBy?.Name ?? "(No Name)"
                      : undefined;

                    const title = `${
                      item.DataObject.PersonName ?? "No Name"
                    } signed in ${
                      impersonate ? `(impersonated by ${impersonate})` : ""
                    }`;

                    let subTitle =
                      "On " +
                      dayjs(item.LastUpdated).format("MMMM Do YYYY, h:mm:ss a");

                    if (item.DataObject.LocationFound) {
                      subTitle =
                        subTitle + ` from IP Address: ${item.DataObject.IP}`;
                    }

                    return (
                      <ListItem
                        key={item.id}
                        title={title}
                        subTitle={subTitle}
                        icon="mdi-earth"
                        optionsButton={null}
                      />
                    );
                  })}
                <AppTablePagination
                  records={records}
                  pagination={pagination}
                  query={query}
                  setQuery={setQuery}
                  error={error}
                />
              </>
            )}
          </>
        )}
        {tab === "users" && (
          <>
            <div className="pt-3 pb-3 mb-3 border-bottom border-lighter">
              <AppPageHeader
                size="sm"
                title="Manage Users"
                description="Invite and manage users who have access to your cloud"
                action={toggleInviteUserModal}
                actionButtonName="Invite User"
                disabled={!cPermissions.cloud_create_person}
              />
            </div>
            <div>
              <Row>
                <Col xs="12">
                  <div
                    style={{ width: "100%", maxWidth: "700px" }}
                    className="mb-6"
                  >
                    <div className="pl-2">
                      <h4 className="mb-0">Registered Users</h4>
                      <p>
                        <small>
                          All users that are registered in your cloud.
                        </small>
                      </p>
                    </div>
                    {usersLoading ? (
                      <>
                        <SkeletonLoader />
                        <SkeletonLoader />
                        <SkeletonLoader />
                      </>
                    ) : usersMap.registered ? (
                      usersMap.registered.map((user, i) => {
                        const permissionProfile: any = getUserCloudPermissions(
                          user,
                          permissions,
                          tenantID
                        );
                        const isCurrentUser = currentUser?.id === user?.id;

                        return (
                          <Fragment key={i}>
                            <ListItem
                              title={
                                isCurrentUser ? user.Name + " (Me)" : user.Name
                              }
                              subTitle={user.PrimaryContactEmail}
                              icon="mdi-account"
                              badgeTitle={permissionProfile?.Name}
                              badgeColor={
                                permissionProfile?.Owner ? "secondary" : "light"
                              }
                              options={
                                <RegisteredUserOptions
                                  user={user}
                                  userPermissionProfile={permissionProfile}
                                  setManageUser={setManageUser}
                                  isCurrentUser={isCurrentUser}
                                  isCurrentUserOwner={isCurrentUserOwner}
                                  removeUser={() =>
                                    removeUser({
                                      user,
                                      supportTargetCloudID: undefined, // This is NOT a support request.
                                    })
                                  }
                                  transferOwner={setTransferUser}
                                />
                              }
                            />
                            {usersMap.registered?.length - 1 !== i && (
                              <hr className="my-0"></hr>
                            )}
                          </Fragment>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={3}>None.</td>
                      </tr>
                    )}
                  </div>
                </Col>
              </Row>
              {usersMap.pending && usersMap.pending?.length > 0 && (
                <div
                  style={{ width: "100%", maxWidth: "700px" }}
                  className="mb-5"
                >
                  <div className="pl-2">
                    <h4 className="mb-0">Pending Users</h4>
                    <p>
                      <small>
                        All users that have been invited to your cloud.
                      </small>
                    </p>
                  </div>
                  {usersMap.pending?.map((user, i) => (
                    <Fragment key={i}>
                      <ListItem
                        title={user.PrimaryContactEmail}
                        icon="mdi-account"
                        badgeTitle={"Pending"}
                        options={
                          <PendingUserOptions
                            user={user}
                            resendInvite={resendInvite}
                            removeInvite={(user: any, invitation = true) =>
                              removeUser({
                                user,
                                invitation,
                                supportTargetCloudID: undefined, // This is NOT a support request.
                              })
                            }
                          />
                        }
                      />
                      {usersMap.pending?.length - 1 !== i && (
                        <hr className="my-0"></hr>
                      )}
                    </Fragment>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </Container>
      <InviteUserDrawer
        open={inviteUserModal}
        onClose={toggleInviteUserModal}
        permissions={permissions}
        supportTargetCloudID={undefined} // This is NOT a Support Request.
      />
      <ManageUserPermissionsDrawer
        permissions={permissions}
        open={manageUser !== null}
        onClose={() => setManageUser(null)}
        selectedUser={manageUser}
        getPermissionID={handleGetPermissionID}
        type="Cloud"
        updatePermissions={updateUserCloudPermissions}
        updatingPermissions={updatingPermissions}
        updatePermissionsError={updatePermissionsError}
        supportTargetCloudID={undefined} // This is NOT a support request.
      />
      <TransferOwnershipDrawer
        permissions={permissions}
        open={transferUser !== null}
        onClose={() => setTransferUser(null)}
        newUser={transferUser as any}
        type="Cloud"
        assignOwnership={assignCloudOwnership}
        assigningOwnership={assigningCloudOwnership}
        assignOwnershipError={assignCloudOwnershipError}
      />
    </>
  );
};

export default SecurityAndAccess;
